<template>
  <section class="booking-reservation-details" :class="{ desktop: $isDesktop }">
    <div class="booking-pitch" :class="{ byo: pitchType.isByo }">
      <pu-category-icons :categories="categories" />
      <div class="pitch-info">
        <span>{{ pitchTypeName }}</span>
        <unit-per-pitch-popup
          v-if="pitchType.isByo"
          :one-unit-per-pitch="pitchType.oneUnitPerPitch"
          :allowed-subcategories="pitchType.allowedSubcategories"
        />
        <span class="party-row" v-if="booking.adults || booking.childAges">
          <booking-party-icon
            :adults="booking.adults"
            :child-ages="booking.childAges"
          />
          <span class="party-text">{{ booking.party }}</span>
        </span>
      </div>
    </div>

    <div class="booking-dates">
      <div class="dates arrival">
        <div class="dates-line">
          <img
            class="icon"
            src="@/assets/images/calendar-star.svg"
            alt=""
            width="17"
            height="17"
          />
          <span class="line"></span>
        </div>
        <span class="date">
          <pu-utc-date
            :date="booking.arrive"
            :options="{ weekday: 'long', day: 'numeric', month: 'short' }" />
          <pu-utc-date
            tag="span"
            options="year"
            :date="booking.arrive"
            class="year"
        /></span>
        <booking-date-changed
          class="date-changed"
          v-if="booking.changes && booking.changes.arrive"
          :original-date="booking.changes.arrive.originalDate"
          :changed-date="booking.changes.arrive.changedDate"
        />

        <div v-if="arrivalTimesVisible" class="times">
          <translate> Arrive </translate>
          <i18n-t
            v-if="booking.arrivalFrom || booking.arrivalTo"
            tag="time"
            keypath="{fromTime} - {toTime}"
          >
            <template #fromTime>
              <template v-if="booking.arrivalFrom">
                {{ booking.arrivalFrom }}
              </template>
              <translate v-else> Anytime </translate>
            </template>
            <template #toTime>
              <template v-if="booking.arrivalTo">
                {{ booking.arrivalTo }}
              </template>
              <translate v-else> Anytime </translate>
            </template>
          </i18n-t>
          <translate v-else> Anytime </translate>

          <span v-if="arrivalTimeVisible" class="arrival-time-note">
            {{ booking.arrivalTime }}
          </span>
        </div>
      </div>

      <div class="dates depart">
        <div class="dates-line">
          <img
            src="@/assets/images/moon-solid.svg"
            class="icon"
            width="12"
            height="13"
            alt=""
          />
          <translate tag="span" :tt="{ nights: nightsCount }">
            nights {nights}
          </translate>
        </div>
        <span class="date">
          <pu-utc-date
            :date="booking.depart"
            :options="{ weekday: 'long', day: 'numeric', month: 'short' }" />
          <pu-utc-date
            tag="span"
            class="year"
            :date="booking.depart"
            options="year"
        /></span>
        <booking-date-changed
          class="date-changed"
          v-if="booking.changes && booking.changes.depart"
          :original-date="booking.changes.depart.originalDate"
          :changed-date="booking.changes.depart.changedDate"
        />

        <span v-if="departFromVisible" class="times">
          <translate> Depart by </translate>
          <time v-if="booking.departFrom">
            {{ booking.departFrom }}
          </time>
          <translate v-else> Anytime </translate>
        </span>
      </div>
    </div>
    <p v-if="arrivalTimesNote" class="arrival-times-note preserve-linebreaks">
      {{ arrivalTimesNote }}
    </p>

    <div class="jump-links">
      <a href="#site-information" class="account-action sml">
        <img
          class="icon"
          src="@/assets/images/google-maps-icon.svg"
          width="17"
          height="24"
          alt=""
        />
        <translate> Get directions</translate>
      </a>
      <balance-status-button
        v-if="!isPastBooking && !isBookingPaid"
        tag="a"
        href="#payment"
        :payment-info="balanceBooking"
      />
    </div>
  </section>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { daysBetween } from '~/utility/date/relative'
import { combineCategories } from '~/utility/pitchtype'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { SimpleMoney } from '../../../utility/money'

interface PitchTypeCategory {
  name: string
  slug: string
}

interface Booking {
  adults: number
  address: string | null
  arrivalFrom: string | null
  arrivalTime: string
  arrivalTo: string | null
  arrive: string
  campsiteUsePermittedArrivalDepartTimes: boolean
  campsite: Campsite
  carRegistrationNumber: string
  changes: {
    arrive?: {
      originalDate: string
      changedDate: string
    }
    depart?: {
      originalDate: string
      changedDate: string
    }
  } | null
  childAges: number[]
  city: string | null
  county: string | null
  departFrom: string | null
  depart: string
  dimensionsDepth: string | null
  dimensionsWidth: string | null
  dogs?: number | null
  estimatedTimeOfArrival: string
  groupName: string
  namesOfAllPartyMembers: string
  postcode: string | null
  specialRequests: string
  typeAndApproxSizeOfUnit: string
  unitType: {
    id: string
    name: string
    slug: string
  } | null
  balanceDueDate: string
  paymentStatus: string
  stripecampsitepayment?: {
    statusDisplay: string
  }
  remainder: SimpleMoney
  pitchtypeNameWithUnitNumber?: string
}

interface Campsite {
  timezone: string
}

interface PitchType {
  allowedSubcategories: string[]
  bedroomsText: string
  campsite: Campsite
  categories: PitchTypeCategory[]
  description: string
  dimensions: string
  groundType: string
  isByo: boolean
  name: string
  negativeCategories: PitchTypeCategory[]
  oneUnitPerPitch: boolean
  requireCarRegistration: boolean
  requirePartyNames: boolean
  twinAxleAccepted: boolean
}

@Component
class BookingReservationDetails extends Vue {
  @Prop({ required: true })
  pitchType: PitchType

  @Prop({ required: true })
  booking: Booking

  @Prop({ default: true })
  isBookingPaid: boolean

  @Prop({ default: true })
  isPastBooking: boolean

  @Prop({ required: false })
  arrivalTimesNote: string

  get arrivalTimesVisible() {
    return this.booking.campsiteUsePermittedArrivalDepartTimes
  }

  get departFromVisible() {
    return this.booking.campsiteUsePermittedArrivalDepartTimes
  }

  get arrivalTimeVisible() {
    return this.booking.arrivalTime && this.booking.arrivalTime !== '--'
  }

  get pitchTypeName() {
    return this.booking.pitchtypeNameWithUnitNumber || this.pitchType.name
  }

  get nightsCount() {
    return daysBetween(this.booking.arrive, this.booking.depart)
  }

  get balanceBooking() {
    return {
      ...this.booking,
      payment: {
        timing: this.booking.paymentInfo?.paymentTiming,
        dueDate: this.booking.balanceDueDate,
        status: this.booking.paymentStatus
          ? this.booking.paymentStatus
          : this.booking.stripecampsitepayment?.status,
        statusDisplay: this.booking.stripecampsitepayment?.statusDisplay,
      },
    }
  }

  get categories() {
    try {
      return combineCategories(
        this.pitchType.categories,
        this.pitchType.negativeCategories,
      )
    } catch (e) {
      console.error(e)
      return []
    }
  }

  get userAddress() {
    return [
      this.booking.address,
      this.booking.city,
      this.booking.county,
      this.booking.postcode,
    ]
      .filter((el) => el !== '')
      .join(', ')
  }
}

export default toNative(BookingReservationDetails)
</script>

<style scoped>
.booking-reservation-details {
  padding: 0.5em 0.6em 0.6em 0.6em;
  border: 1px solid var(--colour-page-background);
  border-radius: var(--border-radius);
  margin: 0 var(--edge-margin) 1em;
  width: calc(100% - 2 * var(--edge-margin));
  overflow: hidden;
}

.booking-reservation-details p {
  font-size: var(--body);
}

.category-icons {
  display: inline-flex;
  flex-shrink: 0;
  flex-direction: row;
  justify-self: center;
  gap: 0.25em;
}

.category-icons :deep(img),
.category-icons :deep(img ~ img) {
  margin: 0;
}

.booking-pitch {
  font-size: var(--title);
  color: var(--colour-dark-blue);
  font-weight: bold;
  display: flex;
  align-items: start;
  gap: 0.5em;
  line-height: 1;
  padding: 0 1em 0.35em 0;
  border-bottom: 1px solid var(--colour-page-background);
}

.booking-pitch.byo {
  padding-right: 1em;
  position: relative;
}

.booking-pitch.byo :deep(.pu-popover) {
  position: absolute;
  right: -0.5em;
  top: -0.1em;
}

.pitch-info {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.party-row {
  font-weight: normal;
  font-size: var(--body);
  margin: 0.4em 0 0 0;
  display: flex;
  align-items: baseline;
  gap: 0.3em;
}

.party-row :deep(img.icon) {
  max-height: 1em;
  filter: hue-rotate(77deg) brightness(0.7);
  width: auto;
}

.party-text {
  flex: 0 0 100%;
  line-height: 1.1;
}

.pt-row {
  display: grid;
  grid-template-columns: 19px 1fr;
  place-items: start start;
  gap: 0.35em;
  margin-bottom: 0.5em;
}

.pt-row img,
.pt-row :deep(img) {
  place-self: start center;
  max-width: none;
}

.booking-dates {
  display: flex;
  justify-content: flex-start;
  margin: var(--edge-margin) 0;
  width: 100%;
  border-bottom: 1px solid var(--colour-table-borders);
  column-gap: 0.4em;
}

.dates {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  width: 50%;
  font-size: var(--body);
}

.dates-line {
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 0.4em;
  align-items: center;
  width: 100%;
  height: 1em;
  margin: 0 0 0.35em 0;
}

.dates-line .icon {
  width: auto;
  height: 1.2em;
}

.depart .icon {
  height: 1em;
}

.arrival-times-note {
  border-bottom: 1px solid var(--colour-table-borders);
  padding-bottom: 0.5em;
  margin: -0.5em 0 var(--edge-margin) 0;
}

.line {
  display: block;
  width: 100%;
  height: 3px;
  margin: 0 0 0 0.2em;
  border-top: 3px dashed var(--colour-page-background-light);
}

time {
  font-weight: bold;
  color: var(--colour-dark-blue);
}

.booking-dates time {
  display: inline-block;
}

.date-changed {
  font-weight: normal;
  color: var(--colour-text-light);
}

.times,
.times time,
.year {
  color: var(--colour-text-light);
}

.times {
  margin: 0 0 0.5em;
}

.arrival-time-note {
  font-size: 0.85em;
  display: block;
}

.jump-links {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5em;
}

.account-action.sml {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5em;
  color: var(--colour-dark-blue);
  border: 1px solid #bababa;
  width: 100%;
  overflow-wrap: anywhere;
}

.account-action.sml :deep(.icon) {
  height: 1.35em;
}

.booking-reservation-details.desktop {
  padding: 0.75em 1em;
  margin: 2em var(--edge-margin);
}

.booking-reservation-details.desktop .pitch-info {
  font-size: var(--large);
}

.booking-reservation-details.desktop .party-row {
  font-size: initial;
}

.booking-reservation-details.desktop .dates {
  font-size: inherit;
}

.booking-reservation-details.desktop .account-action.sml {
  max-width: 50%;
}
</style>
